import type { BodyMeasures } from "@/contexts/DietContext";

const SESSION_KEY = "pendingMeasures";
const LOCAL_KEY = "pendingMeasuresBackup";
const TTL_MS = 24 * 60 * 60 * 1000;

interface PendingMeasuresPayload {
  measures: BodyMeasures;
  savedAt: number;
}

function isValidMeasures(value: unknown): value is BodyMeasures {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.weight === "number" &&
    typeof v.height === "number" &&
    typeof v.age === "number" &&
    (v.sex === "male" || v.sex === "female") &&
    (v.goal === "lose" || v.goal === "maintain" || v.goal === "gain")
  );
}

function parsePayload(raw: string | null): PendingMeasuresPayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);

    // Legacy shape: bare measures object
    if (isValidMeasures(parsed)) {
      return { measures: parsed, savedAt: Date.now() };
    }

    if (
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.savedAt === "number" &&
      isValidMeasures(parsed.measures)
    ) {
      if (Date.now() - parsed.savedAt > TTL_MS) return null;
      return parsed as PendingMeasuresPayload;
    }
  } catch {
    return null;
  }
  return null;
}

export function savePendingMeasures(measures: BodyMeasures) {
  const payload: PendingMeasuresPayload = { measures, savedAt: Date.now() };
  const raw = JSON.stringify(payload);
  try {
    sessionStorage.setItem(SESSION_KEY, raw);
  } catch {
    // ignore quota / privacy mode
  }
  try {
    // Backup survives tab switches (email confirmation, PIX redirect, etc.)
    localStorage.setItem(LOCAL_KEY, raw);
  } catch {
    // ignore quota / privacy mode
  }
}

export function loadPendingMeasures(): BodyMeasures | null {
  const sessionPayload = parsePayload(sessionStorage.getItem(SESSION_KEY));
  if (sessionPayload) {
    // Refresh local backup
    try {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(sessionPayload));
    } catch {
      // ignore
    }
    return sessionPayload.measures;
  }

  const localPayload = parsePayload(localStorage.getItem(LOCAL_KEY));
  if (localPayload) {
    // Re-hydrate session for the rest of this tab
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(localPayload));
    } catch {
      // ignore
    }
    return localPayload.measures;
  }

  clearPendingMeasures();
  return null;
}

export function clearPendingMeasures() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
  try {
    localStorage.removeItem(LOCAL_KEY);
  } catch {
    // ignore
  }
}

export function hasPendingMeasures() {
  return !!loadPendingMeasures();
}
